// frontend/src/contexts/AuthContext.jsx
// Contexte d'authentification avec mot de passe client + admin HR_users
// 73+ Papy - Décembre 2025

import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext(null);

// Configuration API
const API_BASE = process.env.REACT_APP_API_URL || '/api';

export const AuthProvider = ({ children }) => {
  // ==========================================
  // ÉTATS
  // ==========================================
  const [user, setUser] = useState(() => {
    try {
      const savedUser = localStorage.getItem('farm_user');
      return savedUser ? JSON.parse(savedUser) : null;
    } catch {
      return null;
    }
  });

  const [adminUser, setAdminUser] = useState(() => {
    try {
      const savedAdmin = localStorage.getItem('farm_admin_user');
      return savedAdmin ? JSON.parse(savedAdmin) : null;
    } catch {
      return null;
    }
  });

  const [token, setToken] = useState(() => {
    return localStorage.getItem('farm_token') || null;
  });

  const [adminToken, setAdminToken] = useState(() => {
    return localStorage.getItem('farm_admin_token') || null;
  });

  const [loading, setLoading] = useState(false);

  // ==========================================
  // SYNCHRONISATION AVEC LOCALSTORAGE
  // ==========================================
  useEffect(() => {
    if (user) {
      localStorage.setItem('farm_user', JSON.stringify(user));
    } else {
      localStorage.removeItem('farm_user');
    }
  }, [user]);

  useEffect(() => {
    if (token) {
      localStorage.setItem('farm_token', token);
    } else {
      localStorage.removeItem('farm_token');
    }
  }, [token]);

  useEffect(() => {
    if (adminUser) {
      localStorage.setItem('farm_admin_user', JSON.stringify(adminUser));
    } else {
      localStorage.removeItem('farm_admin_user');
    }
  }, [adminUser]);

  useEffect(() => {
    if (adminToken) {
      localStorage.setItem('farm_admin_token', adminToken);
    } else {
      localStorage.removeItem('farm_admin_token');
    }
  }, [adminToken]);

  // ==========================================
  // AUTHENTIFICATION CLIENT
  // ==========================================
  const loginClient = async (identifier, type, password) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/auth/client`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identifier, type, password })
      });

      const data = await response.json();

      if (data.success) {
        setUser(data.user);
        setToken(data.token || data.user.qrcode);
        return { success: true, user: data.user };
      } else {
        return { success: false, error: data.error || 'Échec de connexion' };
      }
    } catch (err) {
      console.error('Erreur loginClient:', err);
      return { success: false, error: 'Erreur de connexion au serveur' };
    } finally {
      setLoading(false);
    }
  };

  // ==========================================
  // AUTHENTIFICATION ADMIN
  // ==========================================
  const loginAdmin = async (login, password) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/auth/admin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ login, password })
      });

      const data = await response.json();

      if (data.success) {
        setAdminUser(data.user);
        setAdminToken(data.token);
        return { success: true, user: data.user };
      } else {
        return { success: false, error: data.error || 'Identifiants incorrects' };
      }
    } catch (err) {
      console.error('Erreur loginAdmin:', err);
      return { success: false, error: 'Erreur de connexion au serveur' };
    } finally {
      setLoading(false);
    }
  };

  // ==========================================
  // DÉCONNEXION
  // ==========================================
  const logoutClient = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('farm_user');
    localStorage.removeItem('farm_token');
  };

  const logoutAdmin = () => {
    setAdminUser(null);
    setAdminToken(null);
    localStorage.removeItem('farm_admin_user');
    localStorage.removeItem('farm_admin_token');
  };

  const logout = () => {
    logoutClient();
    logoutAdmin();
  };

  // ==========================================
  // VÉRIFICATIONS
  // ==========================================
  const isAuthenticated = () => {
    return !!(user && token);
  };

  const isAdminAuthenticated = () => {
    return !!(adminUser && adminToken);
  };

  // ==========================================
  // MISE À JOUR USER
  // ==========================================
  const updateUser = (updates) => {
    if (user) {
      const updatedUser = { ...user, ...updates };
      setUser(updatedUser);
    }
  };

  // ==========================================
  // VALEUR DU CONTEXTE
  // ==========================================
  const value = {
    // États
    user,
    adminUser,
    token,
    adminToken,
    loading,
    
    // Actions client
    loginClient,
    logoutClient,
    updateUser,
    
    // Actions admin
    loginAdmin,
    logoutAdmin,
    
    // Logout global
    logout,
    
    // Vérifications
    isAuthenticated,
    isAdminAuthenticated
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// ==========================================
// HOOK PERSONNALISÉ
// ==========================================
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth doit être utilisé dans un AuthProvider');
  }
  return context;
};

export default AuthContext;